import { useMemo, useState } from 'react';
import { Copy, X } from 'lucide-react';
import { buildUtmUrl } from './exports';

/**
 * The link builder.
 *
 * It exists for one reason: `utm_source` gets typed three different ways by
 * three different people, and the Campaigns card then reports Google, google
 * and "Google Ads" as three separate sources that each look too small to matter.
 * Nothing here reaches the server — it is a text field that produces a link.
 */
export function UtmBuilder({ onClose }: { onClose: () => void }) {
  const [url, setUrl] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);

  const built = useMemo(
    () => buildUtmUrl({ url, source, medium, campaign, content }),
    [url, source, medium, campaign, content],
  );

  const copy = async () => {
    if (!built) return;
    try {
      await navigator.clipboard.writeText(built);
      setCopied(true);
    } catch {
      // Clipboard needs a secure context; the link is on screen either way.
      setCopied(false);
    }
  };

  const field = (
    label: string,
    value: string,
    set: (next: string) => void,
    placeholder: string,
    hint?: string,
  ) => (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        className="input"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          set(event.target.value);
          setCopied(false);
        }}
      />
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );

  return (
    <section className="an-card an-builder">
      <div className="an-card-head">
        <h2 className="an-card-title">Tag a link</h2>
        <button type="button" className="an-card-export" onClick={onClose} aria-label="Close">
          <X size={13} />
        </button>
      </div>
      <div className="an-builder-grid">
        {field('Where it points', url, setUrl, 'example.com/airport-transfers')}
        {field('Where you are putting it', source, setSource, 'newsletter', 'The site or publication — google, newsletter, partner name.')}
        {field('How', medium, setMedium, 'email', 'email, cpc, social, banner.')}
        {field('Which campaign', campaign, setCampaign, 'spring-airport')}
        {field('Which version (optional)', content, setContent, 'header-button', 'Tells two links in the same email apart.')}
      </div>
      {built ? (
        <>
          <pre className="embed-snippet">
            <code>{built}</code>
          </pre>
          <div className="manage-card-actions manage-card-actions-start">
            <button className="btn btn-primary" onClick={() => void copy()}>
              <Copy size={14} />
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </>
      ) : (
        <p className="an-caveat">
          Fill in at least the address and where you are putting it. Values are tidied as you type —
          lower-cased, spaces hyphenated — so the same campaign typed two ways still lands in one row
          on the report.
        </p>
      )}
    </section>
  );
}
